import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/react-hooks';
import type {
  Action,
  ActionFn,
  DataGridProps,
  SelectionMode,
  SetIsLoading,
} from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import DataGrid from '#/common/components/DataGrid';
import { getAssetTypeList } from '#/graphql/queries';
import useAssetTypeDelete from '../../hooks/useDeleteAssetTypeMutation';
import columns from './columns';
import { actions } from './actions';
import { AssetType } from '#/graphql/types';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import { inUseStatuses } from '../AssetType.Mappings';

const getAssetTypeListQuery = gql(getAssetTypeList);

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-asset-types'],
  [UserRole.trane_admin]: ['delete-asset-types'],
  [UserRole.technician]: [] as string[],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['new'],
  [UserRole.trane_admin]: ['new'],
  [UserRole.technician]: [] as string[],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<AssetType>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const AssetTypeDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { deleteAssetType } = useAssetTypeDelete();

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these asset types?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteAssetType(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteAssetType],
  );

  const { user } = useSession();

  const role = user?.role as UserRole | undefined;

  const userSelectedActions: ActionFn<AssetType>[] = useMemo(() => {
    const res: ActionFn<AssetType>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-asset-types')) {
      res.push({
        key: 'delete-asset-types',
        icon: 'trash',
        text: 'Delete',
        disabled: (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (assetType) =>
              assetType.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(assetType.thirdPartyId && assetType.thirdPartySource),
          );
          let count = filteredItemsForDelete?.length;
          return count < 1;
        },
        onClick: async (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (assetType) =>
              assetType.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(assetType.thirdPartyId && assetType.thirdPartySource),
          );

          if (filteredItemsForDelete.length < e.selectedItems.length) {
            const sure = await confirm(
              'error',
              `Some selected asset types have been in active status and can not be deleted.`,
            );
            if (!sure) {
              return;
            }
          }

          await deleteItem(
            filteredItemsForDelete.map((assetType) => assetType.id!),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }

    return res;
  }, [role]);

  const gridActions = useMemo<Action<AssetType>[]>(() => {
    return actions
      .filter((a) => typeof a !== 'object' || (role && gridActionsPerRole[role]?.includes(a?.key)))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };

        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  if (!role) return null;

  return (
    <DataGrid
      id="asset-types"
      pluralText="Asset Types"
      singularText="Asset Type"
      columns={columns}
      recordUrl="/assets/types/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getAssetTypeListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(AssetTypeDataGrid);
